// src/views/WhiteboardDock.jsx
// THE WHITEBOARD FROM ANY SCREEN — a note bubble pinned to the BOTTOM-LEFT
// corner of every staff screen, the mirror image of the Ask-the-Library chat
// bubble (src/views/ChatDock.jsx, bottom-right). Reece, 10/2: "a bubble on
// the opposite side of the chatbot … to save text as if it's on a white
// board". Click it and a small panel opens over whatever is on screen;
// minimize it (–, or Esc) and it's a bubble again. The badge is how many
// notes are on the board.
//
// It is a second window onto the SAME board as the Command Center's
// Whiteboard panel (src/views/Whiteboard.jsx — one store per person in
// src/whiteboard.js), so App hides it on the Command Center itself (the board
// is right there) unless the New Call console is open over it — the chat
// bubble's rule for the Library screen. Also hidden while the nav drawer or
// the automations panel is open. On the Lender Library screen the bubble sits
// higher (`lifted`) so it never covers the left end of that page's Ask box,
// which sticks to the bottom of the screen and grows with the question: the
// Ask box publishes its height as --lf-ask-h (LenderLibrary AskTab) and the
// bubble sits that far up. There it also sits UNDER the Library's own
// overlays (page viewer z 300, its modals z 320) — they cover the page, so
// they cover the bubble too. Over the New Call console the bubble stays in
// its corner: the console's sticky action bar keeps that corner clear
// (CallConsole `dockLeft`), so it never covers "Run the Numbers →".
//
// Esc inside the panel minimizes it — and ONLY it: like ChatDock, the capture
// listener is registered once, before the New Call console's own, so it can't
// also close a call. Esc while a note is being edited cancels that edit
// instead and leaves the panel open (handed to the editor as a
// "wb-cancel-edit" event, since the key never reaches the box itself).
//
// Narrow screens: both bubbles sit in the bottom corners. When either panel
// opens it says so on window ("lf-dock-open" {dock} — src/ui.jsx); when the
// two panels can't sit side by side (docksCollide, < 840px) the other one
// minimizes — and when the window is narrowed with both open, the one opened
// first does (useDockRoom). On phones (≤ 560px) the open panel spans the
// width, 12px gutters.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { C } from '../theme';
import { announceDockOpen, useDockRoom } from '../ui';
import { getWhiteboardStore, useWhiteboard } from '../whiteboard';
import { WhiteboardAdd, WhiteboardNotes } from './Whiteboard';

const FONT = "'Mulish',system-ui,sans-serif";
// Same layer as the chat bubble (above the New Call console at 400, below its
// disposition modal at 420, the inbound-call toast at 500 and every screen's
// modals). An open panel goes one above, so it covers the other bubble.
const Z = 405;
// Lifted on the Library screen: above the page and the nav drawer (200 / 210),
// below the Library's page viewer (300) and its modals (320).
const Z_UNDER_LIBRARY = 290;
const noteIcon = <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true"><path d="M15.5 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V8.5z" /><path d="M15 3v6h6" /><line x1="7" y1="13" x2="15" y2="13" /><line x1="7" y1="17" x2="12" y2="17" /></svg>;
const headBtn = { height: 30, minWidth: 30, padding: 0, width: 30, borderRadius: 8, border: "1px solid " + C.border, background: "transparent", color: C.navy, cursor: "pointer", fontFamily: FONT, fontSize: 16, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };

// staff: whose board. hidden: show nothing (Command Center, nav drawer,
// automations) — the board lives in the store, nothing is lost, and the
// panel comes back open if it was. lifted: the Library screen (see above).
function WhiteboardDock({ staff, hidden, lifted }) {
  const who = staff || "reece";
  const store = getWhiteboardStore(who);
  const [open, setOpen] = useState(false);
  const count = useWhiteboard(who, function (s) { return s.active.length; });
  const rootRef = useRef(null);
  const addRef = useRef(null);
  const launcherRef = useRef(null);
  const focusRef = useRef(false);              // cursor into the add box when he opens it (not when it reappears)
  const openRef = useRef(open); openRef.current = open;
  const shown = open && !hidden;

  // On screen: re-read the board (another device may have written), and the
  // cursor goes into the add box if he just opened it. Off screen (closed,
  // hidden, unmounted): anything half-saved goes out now.
  useEffect(function () {
    if (!shown) return undefined;
    store.refresh();
    if (focusRef.current) {
      focusRef.current = false;
      const ta = addRef.current; if (ta) { try { ta.focus({ preventScroll: true }); } catch (e) { ta.focus(); } }
    }
    return function () { store.flush(); };
  }, [shown, store]);

  const close = useCallback(function (refocus) {
    setOpen(false);
    if (refocus) setTimeout(function () { if (launcherRef.current) launcherRef.current.focus(); }, 0);
  }, []);
  const openIt = function () {
    focusRef.current = true;
    setOpen(true);
    announceDockOpen("whiteboard");
  };
  // Esc inside the panel: minimize — or, in a note being edited, cancel that
  // edit. Registered once, before the New Call console's listener.
  useEffect(function () {
    const h = function (e) {
      if (e.key !== "Escape" || !openRef.current) return;
      const root = rootRef.current;
      if (!root || !e.target || !root.contains(e.target)) return;
      e.stopImmediatePropagation(); e.preventDefault();
      const ed = typeof e.target.closest === "function" ? e.target.closest("[data-wb-editor]") : null;
      if (ed) { try { ed.dispatchEvent(new CustomEvent("wb-cancel-edit")); } catch (x) { /* ignore */ } return; }
      close(true);
    };
    window.addEventListener("keydown", h, true);
    return function () { window.removeEventListener("keydown", h, true); };
  }, [close]);
  // The chat panel opened where the two can't both fit, or the window
  // narrowed with both open: make room (src/ui.jsx).
  const minimize = useCallback(function () { setOpen(false); }, []);
  useDockRoom("whiteboard", open, minimize);

  // Lifted (the Library screen): just above that page's Ask box, however tall
  // the question has grown (--lf-ask-h; 92px = its one-line height), and under
  // the Library's page viewer / modals.
  const css = `@keyframes lf-wb-in{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.lf-wb{position:fixed;left:20px;bottom:20px;z-index:${Z};font-family:${FONT};}
.lf-wb.is-lifted{bottom:calc(var(--lf-ask-h, 92px) + 20px);z-index:${Z_UNDER_LIBRARY};}
.lf-wb.is-open{z-index:${Z + 1};}
.lf-wb-panel{width:380px;max-height:min(620px,calc(100vh - 100px));display:flex;flex-direction:column;background:${C.bg};border:1px solid ${C.border};border-radius:16px;box-shadow:0 18px 50px rgba(6,26,48,0.28);overflow:hidden;animation:lf-wb-in .16s ease-out;}
.lf-wb-launch{position:relative;width:56px;height:56px;border-radius:50%;border:1px solid ${C.cream2};cursor:pointer;display:flex;align-items:center;justify-content:center;color:${C.navy};background:linear-gradient(135deg,${C.paper},${C.cream});box-shadow:0 6px 22px rgba(6,26,48,0.24);transition:transform .15s ease,box-shadow .15s ease;}
.lf-wb-launch:hover{transform:translateY(-2px);box-shadow:0 10px 28px rgba(6,26,48,0.32);}
.lf-wb-badge{position:absolute;top:-4px;right:-4px;min-width:20px;height:20px;padding:0 5px;box-sizing:border-box;border-radius:10px;background:${C.clay};color:#fff;font-size:11px;font-weight:800;line-height:16px;text-align:center;border:2px solid ${C.bg};}
@media (max-width:560px){.lf-wb{left:12px;bottom:12px;}.lf-wb.is-lifted{bottom:calc(var(--lf-ask-h, 92px) + 12px);}.lf-wb.is-open{right:12px;bottom:12px;}.lf-wb-panel{width:auto;max-height:calc(100vh - 88px);}}
@media (prefers-reduced-motion:reduce){.lf-wb-panel{animation:none;}.lf-wb-launch{transition:none;}}`;

  if (hidden) return null;
  const label = count === 1 ? "1 note" : count + " notes";
  if (!open) return (<div ref={rootRef} className={"lf-wb" + (lifted ? " is-lifted" : "")} data-lf-whiteboard="">
    <style>{css}</style>
    <button ref={launcherRef} className="lf-wb-launch" onClick={openIt}
      title={"Whiteboard — your notes" + (count ? " (" + label + ")" : "")}
      aria-label={"Open the Whiteboard" + (count ? " (" + label + ")" : "")}>
      {noteIcon}
      {count > 0 && <span className="lf-wb-badge" data-wb-count="" aria-hidden="true">{count > 99 ? "99+" : count}</span>}
    </button>
  </div>);

  return (<div ref={rootRef} className="lf-wb is-open" data-lf-whiteboard="">
    <style>{css}</style>
    <section className="lf-wb-panel" role="dialog" aria-modal="false" aria-label="Whiteboard">
      {/* ── header — paper, not the chat's navy ── */}
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 9, padding: "10px 10px 10px 14px", background: C.cream, borderBottom: "2px solid " + C.clay, color: C.navy }}>
        <span style={{ display: "flex", color: C.clayDeep }}>{noteIcon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: .2 }}>Whiteboard{count ? <span style={{ fontWeight: 700, color: C.textMuted, fontSize: 11.5 }}> · {label}</span> : null}</div>
          <div style={{ fontSize: 10.5, color: C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Your notes — also on the Command Center</div>
        </div>
        <button onClick={function () { close(true); }} title="Minimize (Esc)" aria-label="Minimize the whiteboard" style={headBtn}>–</button>
      </div>
      {/* ── add a note (stays put) ── */}
      <div style={{ flexShrink: 0, padding: "10px 10px 8px", borderBottom: "1px solid " + C.border }}>
        <WhiteboardAdd staff={who} compact inputRef={addRef} />
      </div>
      {/* ── the board (shared with the Command Center) ── */}
      <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", padding: "10px 10px 12px" }}>
        <WhiteboardNotes staff={who} variant="dock" />
      </div>
    </section>
  </div>);
}

// Memoized: App re-renders constantly (save status, polls); the props are a
// string and two booleans.
const WhiteboardDockMemo = React.memo(WhiteboardDock);
export { WhiteboardDockMemo as WhiteboardDock };
